import { CACHED_DATA } from './constants.js';
import { AGENT_CONFIG } from './prompts.js';

// ─── Shared State ─────────────────────────────────────────────────────────
// Mutable object pattern: import { state } from './chatUI.js'
// then mutate as state.outputLanguage = 'zh'. Do NOT use `export let` —
// importers receive a value snapshot and won't see later reassignments.
export const state = {
    currentMode: null,
    isProcessing: false,
    outputLanguage: 'en'
};

// ─── Pure Utils ───────────────────────────────────────────────────────────
export async function fetchJson(url, keyName) {
    if (!url) return null;
    try {
        const res = await fetch(url);
        if (res.ok) {
            const rawData = await res.json();
            return keyName ? rawData[keyName] : rawData;
        }
    } catch (e) { console.warn(`[${keyName}] Fetch failed:`, e); }
    return null;
}

export function dataNormalizer(raw) {
    const source = (Array.isArray(raw) && raw.length > 0) ? raw[0] : (raw || {});
    if (Array.isArray(source.data)) return source.data;
    return [];
}

// ─── Message Rendering ────────────────────────────────────────────────────
export function addMessage(text, type) {
    const div = document.createElement('div'); div.className = `msg ${type}`;
    div.innerHTML = text.replace(/\n/g, '<br>');
    const area = document.getElementById('messages-area');
    area.appendChild(div);
    area.scrollTop = area.scrollHeight;
    return div;
}

export function updateMessage(el, HTMLText) {
    el.innerHTML = HTMLText.replace(/\n/g, '<br>');
    const area = document.getElementById('messages-area');
    area.scrollTop = area.scrollHeight;
}

// ─── Input Helpers ────────────────────────────────────────────────────────
export function handleKeyUp(e) {
    const val = e.target.value;
    const box = document.getElementById('suggestion-box');
    if (val.endsWith('@')) {
        box.innerHTML = AGENT_CONFIG.map(a => `
            <div class="suggestion-item" onclick="applySuggestion('@${a.name.toLowerCase()}')">
                <span class="suggestion-cmd">@${a.name}</span>
                <span class="suggestion-desc">${a.desc}</span>
            </div>
        `).join('');
        box.style.display = 'block';
    } else if (!val.includes('@')) { box.style.display = 'none'; }
}

export function applySuggestion(cmd) {
    const input = document.getElementById('user-input');
    input.value = input.value.replace(/@$/, '') + cmd + " ";
    document.getElementById('suggestion-box').style.display = 'none';
    input.focus();
}

// ─── Mode & Language ──────────────────────────────────────────────────────
export function showModeSelector() {
    state.currentMode = null;
    document.getElementById('input-container').style.display = 'none';
    const mainMsg = document.getElementById('main-msg');
    mainMsg.innerHTML = `
        <div class="welcome-message">
            <div class="welcome-title">Welcome to ReadSphereGPT!</div>
            <div class="welcome-copy">Please select your preferred Mode:</div>
            <div class="welcome-actions">
                <button class="mode-btn" type="button" data-mode="general">General Mode</button>
                <button class="mode-btn" type="button" data-mode="exploratory">Exploratory Mode</button>
            </div>
        </div>
    `;
    mainMsg.querySelectorAll('[data-mode]').forEach((button) => {
        button.addEventListener('click', () => selectMode(button.dataset.mode));
    });
    document.getElementById('data-status').innerHTML = `Please select a mode to start.`;
}

export function selectMode(mode) {
    state.currentMode = mode;
    document.getElementById('input-container').style.display = 'flex';
    addMessage(`<b>${mode.toUpperCase()} MODE enabled.</b><br>Ask anything or use task tags.`, 'bot');
    updateStatusBar(mode);
}

export function updateStatusBar(activeMode) {
    const statusBar = document.getElementById('data-status');
    statusBar.innerHTML = `
        <span>Current Mode:</span>
        <div class="mode-switch-container">
            <button type="button" class="mini-btn ${activeMode === 'general' ? 'active' : ''}" data-mode="general">General</button>
            <button type="button" class="mini-btn ${activeMode === 'exploratory' ? 'active' : ''}" data-mode="exploratory">Exploratory</button>
        </div>
    `;
    statusBar.querySelectorAll('[data-mode]').forEach((button) => {
        button.addEventListener('click', () => selectMode(button.dataset.mode));
    });
}

export function toggleLanguage() {
    const btn = document.getElementById('lang-toggle-btn');
    if (state.outputLanguage === 'en') {
        state.outputLanguage = 'zh';
        btn.classList.add('active');
        btn.title = "Output Language: Traditional Chinese";
        addMessage("💬 Output language switched to <b>Traditional Chinese</b>.", 'bot');
    } else {
        state.outputLanguage = 'en';
        btn.classList.remove('active');
        btn.title = "Output Language: English";
        addMessage("💬 Output language switched to <b>English</b>.", 'bot');
    }
}

// ─── Routing & Lifecycle ──────────────────────────────────────────────────
export function toggleChat() {
    const chat = document.getElementById('chat-widget');
    chat.style.display = (window.getComputedStyle(chat).display === 'none') ? 'flex' : 'none';
}

export function resetSession() {
    document.getElementById('messages-area').innerHTML = '<div class="msg bot" id="main-msg"></div>';
    showModeSelector();
}

export async function initData() {
    const statusEl = document.getElementById('data-status');
    try {
        const [glossaryData, chapterData] = await Promise.all([
            fetchJson("https://api.npoint.io/14e700c857a248edb614", 'glossary'),
            fetchJson("https://api.npoint.io/c756ac76656ee0595511", 'Full_Chapter_List')
        ]);
        CACHED_DATA.glossary = glossaryData ? JSON.stringify(glossaryData) : "{}";
        CACHED_DATA.chapter = chapterData ? JSON.stringify(chapterData) : "[]";
        statusEl.innerText = `Data ready.`;
        showModeSelector();
    } catch (e) {
        statusEl.innerText = `[System] ERR-101: Initialization Failed`;
        console.error(e);
    }
}
